import { type CurrencyCode, type Locale } from '@fuel-system/contracts';
import { FUEL_MONEY_SCALE, FUEL_TIME_ZONE } from '@fuel-system/domain';
import { Decimal } from 'decimal.js';

/**
 * Display formatting. Values arrive from the API as decimal strings and are
 * only ever converted to a number at the very last step, inside Intl, so no
 * arithmetic is ever done in floating point.
 */

/** Latin digits everywhere: an exchange's numbers must be unambiguous. */
const NUMBERING = 'latn';

export function formatAmount(
  value: string,
  locale: Locale,
  options: { minimumFractionDigits?: number; maximumFractionDigits?: number } = {},
): string {
  const decimal = new Decimal(value);
  return new Intl.NumberFormat(`${locale}-u-nu-${NUMBERING}`, {
    minimumFractionDigits: options.minimumFractionDigits ?? 2,
    maximumFractionDigits: options.maximumFractionDigits ?? 2,
  }).format(decimal.toNumber());
}

export function formatCurrency(
  value: string,
  currency: CurrencyCode,
  locale: Locale,
  fractionDigits = 2,
): string {
  return new Intl.NumberFormat(`${locale}-u-nu-${NUMBERING}`, {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(new Decimal(value).toNumber());
}

/** Rates need more decimals than money, and never fewer. */
export function formatRate(value: string, locale: Locale, fractionDigits = 4): string {
  return new Intl.NumberFormat(`${locale}-u-nu-${NUMBERING}`, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(new Decimal(value).toNumber());
}

/**
 * Fixed-locale formatting for the handful of shared POS-style components
 * (`utils/helpers.ts`) that predate the Arabic-aware formatters above and
 * hardcode `en-US` rather than accepting the active app locale. Rule N2/F2
 * still requires a single construction site for `Intl.NumberFormat` /
 * `Intl.DateTimeFormat`, so these live here rather than in `utils/helpers.ts`
 * — the `en-US` behavior itself is preserved exactly, only the construction
 * site moved and the `-u-nu-latn` subtag (rule N1) was added, which is a
 * no-op for a locale that already renders Western digits.
 */
const STATIC_LOCALE = 'en-US';

export function formatStaticCurrency(
  amount: number,
  currency: CurrencyCode,
  fractionDigits: number,
): string {
  return new Intl.NumberFormat(`${STATIC_LOCALE}-u-nu-${NUMBERING}`, {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount);
}

/**
 * Rule N2/N5: the time half of the same legacy pair. Now the canonical
 * `hh:mm AM|PM` in Asia/Amman, identical in both languages.
 */
export function formatStaticTime(value: string | Date): string {
  return clock12(toDate(value));
}

export function formatStaticNumber(value: number): string {
  return new Intl.NumberFormat(`${STATIC_LOCALE}-u-nu-${NUMBERING}`).format(value);
}

/**
 * Fuel-specific formatting (docs/rebuild/fuel.md section 2 and
 * ui-contract.md section 7): money always shows 5 decimals (decision D14) followed by
 * ` JOD`, from the decimal string, never via `parseFloat`; dates are always
 * `dd/MM/yyyy` and times `hh:mm AM|PM` in Asia/Amman, with Latin digits and
 * Latin AM/PM regardless of the active locale — a receipt or list cell must read the same amount in
 * both languages.
 */
const FUEL_LOCALE = 'en-u-nu-latn';

/** The 5-decimal digits alone, Latin numerals, no ` JOD` suffix (the receipt's own line prices). */
export function formatFuelAmountPlain(value: string): string {
  const decimal = new Decimal(value);
  return new Intl.NumberFormat(`${FUEL_LOCALE}`, {
    minimumFractionDigits: FUEL_MONEY_SCALE,
    maximumFractionDigits: FUEL_MONEY_SCALE,
  }).format(decimal.toNumber());
}

export function formatFuelMoney(value: string): string {
  return `${formatFuelAmountPlain(value)} JOD`;
}

/** A business date (`YYYY-MM-DD`) shown as `dd/MM/yyyy`, with no time-zone conversion. */
export function formatBusinessDate(value: string): string {
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
}

/**
 * Canonical date-time rule (ticket of 3 October 2026): `dd/MM/yyyy hh:mm AM|PM`
 * in Asia/Amman, Latin digits and Latin `AM`/`PM` in both languages. Assembled
 * from `formatToParts` so no locale can reorder the fields or inject bidi marks.
 */
function ammanParts(date: Date): Record<string, string> {
  const parts = new Intl.DateTimeFormat('en-US-u-nu-latn', {
    timeZone: FUEL_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h12',
  }).formatToParts(date);
  const out: Record<string, string> = {};
  for (const part of parts) out[part.type] = part.value;
  return out;
}

function toDate(value: string | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

/** `hh:mm AM|PM` (12:00 AM at midnight, 12:00 PM at noon) in Asia/Amman. */
function clock12(date: Date): string {
  const p = ammanParts(date);
  return `${p['hour']}:${p['minute']} ${(p['dayPeriod'] ?? '').toUpperCase()}`;
}

/** `dd/MM/yyyy` from a real instant (an ISO datetime or a Date), in Asia/Amman. */
export function formatFuelDateFromInstant(value: string | Date): string {
  const date = value instanceof Date ? value : new Date(value);
  const parts = new Intl.DateTimeFormat(`${FUEL_LOCALE}`, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: FUEL_TIME_ZONE,
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? '';
  return `${get('day')}/${get('month')}/${get('year')}`;
}

export function formatFuelTime(value: string | Date): string {
  return clock12(toDate(value));
}

/**
 * A list's date cell: the business date (never shifted) followed by the
 * 12-hour Asia/Amman clock time of the instant, e.g. `28/09/2026 03:45 PM`.
 */
export function formatBusinessDateTime(businessDate: string, instant: string | Date): string {
  return `${formatBusinessDate(businessDate)} ${formatFuelTime(instant)}`;
}

/** `dd/MM/yyyy hh:mm AM|PM` from a real instant, in Asia/Amman. */
export function formatFuelDateTime(value: string | Date): string {
  const date = toDate(value);
  const p = ammanParts(date);
  return `${p['day']}/${p['month']}/${p['year']} ${clock12(date)}`;
}

export function formatStaticPercentage(value: number): string {
  return new Intl.NumberFormat(`${STATIC_LOCALE}-u-nu-${NUMBERING}`, {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
}
